import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Announced,
    DefaultButton,
    IconButton,
    TooltipHost,
} from '@fluentui/react';

interface ICopyButtonProps {
    /** What is being copied, used for the tooltip and screen readers. */
    label: string;
    size?: 'small' | 'medium';
}

type CopySource =
    | { value: string; options?: never }
    | {
          value?: never;
          options: { key: string; text: string; value: string }[];
      };

const COPIED_FEEDBACK_MS = 1200;

/**
 * Copies a value and briefly confirms it.
 *
 * The confirmation matters more than it looks: a copy button that does nothing visible leaves you
 * unsure whether it fired, so people click it repeatedly.
 */
export const CopyButton: React.FC<ICopyButtonProps & CopySource> = ({
    value,
    options,
    label,
    size = 'small',
}) => {
    const [state, setState] = useState<
        'idle' | 'copying' | 'copied' | 'failed'
    >('idle');
    const timer = useRef<number>();
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            window.clearTimeout(timer.current);
        };
    }, []);

    const onCopy = useCallback(async (copyValue: string) => {
        window.clearTimeout(timer.current);
        setState('copying');
        try {
            if (!navigator.clipboard?.writeText)
                throw new Error('Clipboard unavailable');
            await navigator.clipboard.writeText(copyValue);
            if (mounted.current) setState('copied');
        } catch {
            if (mounted.current) setState('failed');
        }
        if (mounted.current)
            timer.current = window.setTimeout(
                () => setState('idle'),
                COPIED_FEEDBACK_MS
            );
    }, []);

    const dimension = size === 'small' ? '24px' : '32px';
    const Button = options ? DefaultButton : IconButton;
    const title =
        state === 'copied'
            ? 'Copied'
            : state === 'failed'
            ? 'Copy failed. Try again.'
            : `Copy ${label}`;

    return (
        <>
            <TooltipHost
                content={title}
                styles={{ root: { display: 'inline-flex', flexShrink: 0 } }}
            >
                <Button
                    text={options ? `Copy ${label}` : undefined}
                    iconProps={{
                        iconName:
                            state === 'copied'
                                ? 'CheckMark'
                                : state === 'failed'
                                ? 'ErrorBadge'
                                : 'Copy',
                    }}
                    title={title}
                    ariaLabel={title}
                    disabled={
                        state === 'copying' ||
                        (options
                            ? !options.some((option) => option.value)
                            : !value)
                    }
                    onClick={(event) => {
                        event.stopPropagation();
                        if (!options) void onCopy(value ?? '');
                    }}
                    menuProps={
                        options
                            ? {
                                  items: options.map((option) => ({
                                      key: option.key,
                                      text: option.text,
                                      disabled: !option.value,
                                      onClick: (event) => {
                                          event?.stopPropagation();
                                          void onCopy(option.value);
                                      },
                                  })),
                              }
                            : undefined
                    }
                    styles={{
                        root: {
                            height: options ? '32px' : dimension,
                            width: options ? 'auto' : dimension,
                            flexShrink: 0,
                            selectors: {
                                '@media (pointer: coarse)': {
                                    minHeight:
                                        size === 'medium' ? 44 : undefined,
                                    minWidth:
                                        size === 'medium' ? 44 : undefined,
                                },
                            },
                        },
                    }}
                />
            </TooltipHost>
            {(state === 'copied' || state === 'failed') && (
                <Announced
                    message={state === 'copied' ? `Copied ${label}` : title}
                />
            )}
        </>
    );
};
